import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import plusIconWhite from "../../assets/icons/plus-icon-white.svg";
import { useCampaignDropdown } from "../../services/campaign/index";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import { FormDataFilterCampaign } from "./campaign-interface";
import "./Campaigns.scss";
import CampaignsFilters from "./CampaignsFilters";
import CampaignsTable from "./CampaignsTable";
import { filterCancelled, handleMultiSelectWithAll } from "./filterAllHelper";
import { useFilteredCampaigns } from "./useFilteredCampaigns";
import { useCountryList } from "../../services/okta";
import useFilterLocalStorage from "../../shared/hooks/useFilterLocalStorage";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

function Campaigns() {
  const { headerRef, sentinelRef, isSticky, headerHeight } = useStickyHeader();
  const navigate = useNavigate();
  const { createLink } = useSimulationParams();

  const defaultVal = {
    search: "",
    status: ["All"],
    cadence: [],
    createBy: [],
    lastEditedBy: [],
    destination: [],
    country: [],
  };

  const methods = useForm<FormDataFilterCampaign>({
      defaultValues: {
        ...defaultVal,
        refreshKey: 0,
        deletePending: false,
      },
  });
  const { watch, setValue} = methods;
  const refreshKey = watch('refreshKey');
  const deletePending = watch('deletePending');


  //match countries coming from get_countries API with row countries
  const { data: countries, isLoading: isCountriesLoading } = useCountryList();

  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));

  const selectedCountries = watch("country") ?? [];

  const availableCountries = Array.isArray(countries)
    ? countries.map((c: any) => c.name || c.code || c)
    : [];

  const countryAdminMap =
    countries?.reduce<Record<string, number>>(
      (acc: { [x: string]: any }, c: { name: string | number; admin: any }) => {
        acc[c.name] = c.admin;
        return acc;
      },
      {}
    ) || {};

  const hasAdminZero = countries ? Object.values(countries).some((c: any) => c.admin === 1) : false;

  const { data: dataCampaignDropdown, isPending: isPendingDataCampaignDropdown } =
    useCampaignDropdown({
      query: {
        country: selectedCountries.length > 0 ? selectedCountries : availableCountries,
        tags: countryTags,
        queryKey: ['campaigns', refreshKey],
      },
      options: {
        enabled: countryTags.length > 0 && availableCountries.length > 0,
      },
    });
  const filterDataCampaignDropdown = JSON.parse(dataCampaignDropdown?.data || "[]");

  const parseDataCampaignDropdown = filterCancelled(filterDataCampaignDropdown);

  const { getFilterValue, updateFilter, filters } = useFilterLocalStorage('campaigns');

  const selectedStatus = ('status' in filters) ? getFilterValue('status') : ['All'];
  const selectedCadence = ('cadence' in filters) ? getFilterValue('cadence') : ['All'];
  const selectedCreatedBy = ('createBy' in filters) ? getFilterValue('createBy') : ['All'];
  const selectedLastEditedBy = ('lastEditedBy' in filters) ? getFilterValue('lastEditedBy') : ['All'];
  const selectedDestination = ('destination' in filters) ? getFilterValue('destination') : ['All'];

  const {
    inputNameValue,
    statusOptions,
    cadenceOptions,
    createdByOptions,
    lastEditedByOptions,
    destinationOptions,
    filteredData,
  } = useFilteredCampaigns({ parseDataCampaignDropdown, watch }, {
    status: selectedStatus,
    cadence: selectedCadence,
    destination: selectedDestination,
    createdBy: selectedCreatedBy,
    lastEditedBy: selectedLastEditedBy,
  });

  const handleMultiChange = (
    current: string[],
    options: string[],
    storageKey: string
  ) => (e: any) => {
    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    const checked = value.length > current.length;
    const changedOption = checked
      ? value.find((opt: string) => !current.includes(opt))
      : current.find((opt: string) => !value.includes(opt));
    const next = handleMultiSelectWithAll(current, changedOption as string, options, checked);
    updateFilter(storageKey, next);
  };

  const handleStatusChange = handleMultiChange(selectedStatus, statusOptions, 'status');
  const handleDatasetChange = handleMultiChange(selectedCadence, cadenceOptions, 'cadence');
  const handleCreatedByChange = handleMultiChange(selectedCreatedBy, createdByOptions, 'createBy');
  const handleLastEditedChange = handleMultiChange(selectedLastEditedBy, lastEditedByOptions, 'lastEditedBy');
  const handleBrandChange = handleMultiChange(selectedDestination, destinationOptions, 'destination');

  const status = selectedStatus.includes('All') ? statusOptions : selectedStatus;
  const cadence = selectedCadence.includes('All') ? cadenceOptions : selectedCadence;
  const createdBy = selectedCreatedBy.includes('All') ? createdByOptions : selectedCreatedBy;
  const lastEditedBy = selectedLastEditedBy.includes('All') ? lastEditedByOptions : selectedLastEditedBy;
  const destination = selectedDestination.includes('All') ? destinationOptions : selectedDestination;

  const clearFilters = () => {
    updateFilter('status', statusOptions);
    updateFilter('cadence', cadenceOptions);
    updateFilter('createBy', createdByOptions);
    updateFilter('lastEditedBy', lastEditedByOptions);
    updateFilter('destination', destinationOptions);
  };

  const initializedFiltersRef = useRef(false);
  useEffect(() => {
    if (initializedFiltersRef.current) return;
    if (
      !isPendingDataCampaignDropdown &&
      statusOptions.length > 0 &&
      cadenceOptions.length > 0 &&
      createdByOptions.length > 0 &&
      lastEditedByOptions.length > 0 &&
      destinationOptions.length > 0
    ) {
      const computeNext = (key: string, options: string[]) => {
        const current = (key in filters) ? getFilterValue(key) : [];
        if (!Array.isArray(current) || current.length === 0) return options;
        if (current.length === 1 && current[0] === 'All') return options;
        return current.filter((v) => options.includes(v));
      };

      updateFilter('status', computeNext('status', statusOptions));
      updateFilter('cadence', computeNext('cadence', cadenceOptions));
      updateFilter('createBy', computeNext('createBy', createdByOptions));
      updateFilter('lastEditedBy', computeNext('lastEditedBy', lastEditedByOptions));
      updateFilter('destination', computeNext('destination', destinationOptions));

      initializedFiltersRef.current = true;
    }
  }, [isPendingDataCampaignDropdown, statusOptions, cadenceOptions, createdByOptions, lastEditedByOptions, destinationOptions]);

  const navigateOnCampaign = () => {
    navigate(createLink("/create/campaigns"));
  };
  if (isCountriesLoading || isPendingDataCampaignDropdown || deletePending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="campaigns">
        <div ref={sentinelRef}></div>
        <div ref={headerRef} className={`campaigns__wrap ${isSticky ? "is-sticky" : ""}`}>
          <div className="campaigns__container">
            <div className="campaigns__container__btn-wrap">
              <div className="campaigns__container__title">Campaigns</div>
              {hasAdminZero && (
                <div className="campaigns__container__btn-wrap__btn">
                  <PrimaryButton
                    variant="contained"
                    onClick={navigateOnCampaign}
                    endIcon={
                      <img
                        className="btn-primary__icon"
                        src={plusIconWhite}
                        alt="Create new campaign"
                      />
                    }
                  >
                    Create new campaign
                  </PrimaryButton>
                </div>
              )}
            </div>
            <Divider variant="middle" className="segments__container__divider" />
            <CampaignsFilters
              handleStatusChange={handleStatusChange}
              handleDatasetChange={handleDatasetChange}
              handleCreatedByChange={handleCreatedByChange}
              handleLastEditedChange={handleLastEditedChange}
              handleBrandChange={handleBrandChange}
              status={status}
              cadence={cadence}
              createdBy={createdBy}
              lastEditedBy={lastEditedBy}
              destination={destination}
              statusOptions={statusOptions}
              cadenceOptions={cadenceOptions}
              createdByOptions={createdByOptions}
              lastEditedByOptions={lastEditedByOptions}
              destinationOptions={destinationOptions}
              clearFilters={clearFilters}
            />
          </div>
        </div>
        <Box className="campaigns__container">
          <CampaignsTable data={filteredData} countryAdminMap={countryAdminMap} stickyTop={headerHeight}/>
        </Box>
      </div>
    </FormProvider>
  );
}

export default Campaigns;